import moment, { Moment } from 'moment'
import React, { useEffect, useRef, useState } from 'react'

import { EuiDatePicker, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiHorizontalRule, EuiInMemoryTable, EuiLoadingContent, EuiLoadingSpinner, EuiPanel, EuiSelect, EuiSpacer, EuiStat, EuiText, EuiTitle, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { BalanceTransaction } from 'api/interfaces/BalanceTransaction'
import { useGetBalancePaymentMethodsQuery, useGetBalancePendingTransactionsQuery, useGetBalanceTransactionsQuery } from 'api/rtkQueryApi/platform/coreBillingApi'
import { useGetOrganizationBillingCustomersQuery } from 'api/rtkQueryApi/platform/organizationsApi'
import { formatCurrency } from 'utils/Formatters'

import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../utils/DefaultPageSize'

const BalanceLedgerDrawer: React.FC = () => {
  const billingCustomersQuery = useGetOrganizationBillingCustomersQuery(undefined)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
  const [fromDate, setFromDate] = useState<Moment>(moment().add(-1, 'month'))
  const [toDate, setToDate] = useState<Moment>(moment())

  const selectedCustomer = billingCustomersQuery.data?.find(c => c.id === selectedCustomerId)
  const accountIdForQuery = selectedCustomer?.accountId ?? ''

  const { data: balancePaymentMethods, isFetching: isBalancePaymentMethodsFetching } = useGetBalancePaymentMethodsQuery({ accountId: accountIdForQuery }, { skip: !selectedCustomer })

  const { data: pendingTransactions, isFetching: isFetchingPendingTransactions } = useGetBalancePendingTransactionsQuery(
    {
      accountId: accountIdForQuery,
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString()
    },
    { skip: !selectedCustomer }
  )

  const { data: transactions, isFetching: isFetchingTransactions } = useGetBalanceTransactionsQuery(
    {
      accountId: accountIdForQuery,
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString()
    },
    { skip: !selectedCustomer }
  )

  useEffect(() => {
    if (billingCustomersQuery.data && billingCustomersQuery.data.length > 0 && !selectedCustomerId) {
      setSelectedCustomerId(billingCustomersQuery.data[0].id)
    }
  }, [billingCustomersQuery.data, selectedCustomerId])

  const todaysAvailableBalance = balancePaymentMethods?.[0]?.availableBalance

  const columns: Array<EuiBasicTableColumn<BalanceTransaction>> = [
    {
      width: '200px',
      name: 'Date',
      field: 'createdAt',
      render: (createdAt: Moment) => moment(createdAt).format('MM/DD/YYYY'),
      sortable: true
    },
    {
      name: 'Description',
      field: 'description',
      sortable: true,
      render: (description: string) => <EuiText size={'s'}>{description}</EuiText>
    },
    {
      width: '200px',
      name: 'Amount',
      field: 'amount',
      align: 'right',
      sortable: true,
      render: (amount: number, item: BalanceTransaction) => {
        let color = 'ghost'
        switch (item.transactionType) {
          case 'Authorized':
            color = 'danger'
            break
          case 'Withdraw':
            color = 'danger'
            break
          case 'Deposit':
            color = 'success'
            break
        }

        return (
          <EuiText size={'s'} color={color}>
            {formatCurrency(amount)}
          </EuiText>
        )
      }
    }
  ]

  const columnsWithRunningBalance = columns.concat([
    {
      width: '200px',
      name: 'Running Balance',
      field: 'balanceAfter',
      align: 'right',
      sortable: true,
      render: (balanceAfter: number) => <EuiText size={'s'}>{formatCurrency(balanceAfter)}</EuiText>
    }
  ])

  const pagination = {
    initialPageSize: DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS
  }

  const sorting = {
    allowNeutralSort: false,
    enableAllColumns: false,
    sort: {
      field: 'createdAt',
      direction: SortDirection.DESC
    }
  }

  const customerOptions =
    billingCustomersQuery.data?.map(customer => ({
      value: customer.id,
      text: customer.name
    })) ?? []

  if (billingCustomersQuery.isLoading || isBalancePaymentMethodsFetching) {
    return <EuiLoadingSpinner size='xl' />
  }

  if (!billingCustomersQuery.data || billingCustomersQuery.data.length === 0) {
    return <EuiText>No billing customers found.</EuiText>
  }

  if (selectedCustomer && (balancePaymentMethods ?? []).length === 0 && !isBalancePaymentMethodsFetching) {
    return <EuiText>No balance payment methods found for this customer.</EuiText>
  }

  return (
    <React.Fragment>
      {billingCustomersQuery.data.length > 1 && (
        <>
          <EuiFlexGroup gutterSize='m' wrap responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiFormRow label='Customer'>
                <EuiSelect
                  options={customerOptions}
                  value={selectedCustomerId}
                  onChange={e => {
                    setSelectedCustomerId(e.target.value)
                  }}
                  style={{ width: 240 }}
                />
              </EuiFormRow>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer />
        </>
      )}
      <EuiFlexGroup wrap gutterSize={'m'} responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiPanel hasShadow={false} hasBorder={true} style={{ width: 200 }}>
            <EuiStat title={formatCurrency(todaysAvailableBalance ?? 0)} titleColor={(todaysAvailableBalance ?? 0) < 0 ? 'danger' : undefined} description={`Today's Available Balance`} titleSize={'m'} />
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer />

      <EuiFlexGroup>
        <EuiFlexItem grow={false}>
          <EuiFormRow label='From Date'>
            <EuiDatePicker selected={fromDate} onChange={date => date && setFromDate(date)} maxDate={toDate} />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFormRow label='To Date'>
            <EuiDatePicker selected={toDate} onChange={date => date && setToDate(date)} minDate={fromDate} maxDate={moment()} />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiHorizontalRule style={{ backgroundColor: 'black' }} />

      <EuiTitle size='s'>
        <h4>Pending Transactions</h4>
      </EuiTitle>

      <EuiSpacer size='m' />

      {isFetchingPendingTransactions ? <EuiLoadingContent lines={3} /> : <EuiInMemoryTable message={'No pending transactions.'} className={'table-color'} items={pendingTransactions ?? []} columns={columns} pagination={pagination} sorting={sorting} />}

      <EuiHorizontalRule style={{ backgroundColor: 'black' }} />

      <EuiTitle size='s'>
        <h4>Posted Transactions</h4>
      </EuiTitle>

      <EuiSpacer size='m' />

      {isFetchingTransactions ? <EuiLoadingContent lines={3} /> : <EuiInMemoryTable message={'No posted transactions.'} className={'table-color'} items={transactions ?? []} columns={columnsWithRunningBalance} pagination={pagination} sorting={sorting} />}
    </React.Fragment>
  )
}

export default BalanceLedgerDrawer
